import React, { useMemo } from 'react';
import { X, Building2, ImageOff } from 'lucide-react';
import type { LeadResult } from '../../types/leads';

/**
 * Props for PropertyComparisonModal
 */
export interface PropertyComparisonModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Properties to compare */
  properties: LeadResult[];
  /** Callback to remove a property from comparison */
  onRemove: (id: string) => void;
}

/**
 * Tier badge component for property cards
 */
const TierBadge: React.FC<{ tier: string }> = ({ tier }) => {
  const tierColors: Record<string, string> = {
    Tier1: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50',
    Tier2: 'bg-blue-500/20 text-blue-400 border border-blue-500/50',
    Tier3: 'bg-amber-500/20 text-amber-400 border border-amber-500/50',
  };

  const color = tierColors[tier] || 'bg-slate-800 text-stone-500 border border-slate-700';
  const label = tier.replace('Tier', 'Tier ');

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${color}`}
    >
      {label}
    </span>
  );
};

/**
 * Thumbnail component with fallback
 */
const Thumbnail: React.FC<{ url?: string | null; address: string }> = ({
  url,
  address,
}) => {
  const [hasError, setHasError] = React.useState(false);

  if (!url || hasError) {
    return (
      <div className="w-full h-32 bg-slate-800 rounded-lg flex items-center justify-center">
        <ImageOff className="w-8 h-8 text-stone-400" />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={address}
      className="w-full h-32 rounded-lg object-cover"
      onError={() => setHasError(true)}
    />
  );
};

/**
 * Format currency for display
 */
function formatCurrency(value: number | null): string {
  if (value === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format percentage for display
 */
function formatPercent(value: number | null): string {
  if (value === null) return '-';
  return `${(value * 100).toFixed(2)}%`;
}

/**
 * Metric row for comparison table
 */
interface MetricRowProps {
  label: string;
  values: (string | number | null)[];
  highlightBest?: 'highest' | 'lowest' | 'none';
  formatter?: (value: number | null) => string;
}

const MetricRow: React.FC<MetricRowProps> = ({
  label,
  values,
  highlightBest = 'none',
  formatter = (v) => (v === null ? '-' : String(v)),
}) => {
  // Find best value index
  const numericValues = values.map((v) =>
    typeof v === 'number' ? v : v === null ? null : parseFloat(v as string)
  );

  const validValues = numericValues.filter((v) => v !== null) as number[];

  let bestValue: number | null = null;
  let worstValue: number | null = null;

  if (highlightBest !== 'none' && validValues.length > 0) {
    bestValue =
      highlightBest === 'highest'
        ? Math.max(...validValues)
        : Math.min(...validValues);
    worstValue =
      highlightBest === 'highest'
        ? Math.min(...validValues)
        : Math.max(...validValues);
  }

  return (
    <tr className="border-b border-slate-700 last:border-b-0">
      <td className="px-4 py-3 text-sm font-medium text-stone-500 bg-slate-800">
        {label}
      </td>
      {values.map((value, idx) => {
        const numValue = numericValues[idx];
        const isBest = bestValue !== null && numValue === bestValue;
        const isWorst =
          worstValue !== null &&
          numValue === worstValue &&
          validValues.length > 1;

        let className = 'px-4 py-3 text-sm text-ivory font-mono text-center';
        if (highlightBest !== 'none') {
          if (isBest) {
            className = 'px-4 py-3 text-sm text-gold font-semibold font-mono bg-gold/10 text-center';
          } else if (isWorst) {
            className = 'px-4 py-3 text-sm text-red-400 font-mono bg-red-500/10 text-center';
          }
        }

        const displayValue =
          typeof value === 'number' ? formatter(value) : value ?? '-';

        return (
          <td key={idx} className={className}>
            {displayValue}
          </td>
        );
      })}
    </tr>
  );
};

/**
 * Property card component
 */
interface PropertyCardProps {
  property: LeadResult;
  onRemove: (id: string) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property, onRemove }) => {
  return (
    <div className="bg-slate-900 border border-slate-700 hover:border-gold/50 rounded-lg overflow-hidden relative group transition-colors">
      {/* Remove button */}
      <button
        onClick={() => onRemove(property.id)}
        className="absolute top-2 right-2 z-10 p-1.5 bg-slate-950/90 rounded-full text-stone-500 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
        aria-label="Remove from comparison"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Thumbnail */}
      <Thumbnail url={property.thumbnail_url} address={property.address} />

      {/* Content */}
      <div className="p-4">
        {/* Address */}
        <h3 className="font-semibold text-ivory text-sm mb-1 line-clamp-2">
          {property.address}
        </h3>

        {/* Location info */}
        {property.neighborhood && (
          <p className="text-xs text-stone-500 mb-2">
            {property.neighborhood}
            {property.zip_code && ` - ${property.zip_code}`}
          </p>
        )}

        {/* Tier badge */}
        <div className="mb-3">
          <TierBadge tier={property.neighborhood_tier} />
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-stone-500">Cap Rate:</span>
            <span className="ml-1 font-mono text-ivory">
              {formatPercent(property.cap_rate)}
            </span>
          </div>
          <div>
            <span className="text-stone-500">Score:</span>
            <span className="ml-1 font-mono text-ivory">
              {property.lead_score ?? '-'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * PropertyComparisonModal component
 *
 * A full-screen modal for comparing 2-4 properties side-by-side.
 * Shows property cards at the top and a metrics comparison table below.
 */
export const PropertyComparisonModal: React.FC<PropertyComparisonModalProps> = ({
  open,
  onClose,
  properties,
  onRemove,
}) => {
  // Calculate grid columns based on property count
  const gridCols = useMemo(() => {
    switch (properties.length) {
      case 2:
        return 'grid-cols-2';
      case 3:
        return 'grid-cols-3';
      case 4:
        return 'grid-cols-4';
      default:
        return 'grid-cols-2';
    }
  }, [properties.length]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-slate-950 border border-slate-700 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] mx-4 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-slate-900">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-gold" />
            <h2 className="text-lg font-semibold text-ivory">
              Compare Properties ({properties.length})
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-stone-500 hover:text-ivory hover:bg-slate-800 rounded-lg transition-colors"
            aria-label="Close comparison"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Property Cards Grid */}
          <div className={`grid ${gridCols} gap-4 mb-8`}>
            {properties.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                onRemove={onRemove}
              />
            ))}
          </div>

          {/* Metrics Comparison Table */}
          <div className="bg-slate-900 border border-slate-700 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-700 bg-slate-800">
              <h3 className="text-[10px] uppercase tracking-wider text-gold font-semibold">
                Metrics Comparison
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-gold uppercase tracking-wider bg-slate-800 w-40">
                      Metric
                    </th>
                    {properties.map((property) => (
                      <th
                        key={property.id}
                        className="px-4 py-3 text-center text-[10px] font-semibold text-gold uppercase tracking-wider"
                      >
                        {property.address.split(',')[0]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <MetricRow
                    label="Cap Rate"
                    values={properties.map((p) => p.cap_rate)}
                    highlightBest="highest"
                    formatter={formatPercent}
                  />
                  <MetricRow
                    label="Lead Score"
                    values={properties.map((p) => p.lead_score)}
                    highlightBest="highest"
                    formatter={(v) => (v === null ? '-' : String(Math.round(v)))}
                  />
                  <MetricRow
                    label="NOI"
                    values={properties.map((p) => p.noi)}
                    highlightBest="highest"
                    formatter={formatCurrency}
                  />
                  <MetricRow
                    label="Market Value"
                    values={properties.map((p) => p.market_value)}
                    highlightBest="none"
                    formatter={formatCurrency}
                  />
                  <MetricRow
                    label="Units"
                    values={properties.map((p) => p.total_units)}
                    highlightBest="none"
                    formatter={(v) => (v === null ? '-' : String(v))}
                  />
                  <MetricRow
                    label="Year Built"
                    values={properties.map((p) => p.year_built)}
                    highlightBest="none"
                    formatter={(v) => (v === null ? '-' : String(v))}
                  />
                  <MetricRow
                    label="Neighborhood Tier"
                    values={properties.map((p) => p.neighborhood_tier)}
                    highlightBest="none"
                  />
                  <MetricRow
                    label="ZIP Code"
                    values={properties.map((p) => p.zip_code ?? '-')}
                    highlightBest="none"
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-slate-700 bg-slate-900">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-ivory bg-slate-800 border border-slate-700 rounded-lg hover:bg-gold/10 hover:border-gold/50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyComparisonModal;
